import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { LogoMark } from "../../components/site/Logo";
import { api } from "../../lib/api";
import { ApplicationsTab, CasesTab } from "./SubmissionsTabs";
import { DocumentsTab, EventsTab, GalleryTab, NewsTab } from "./ContentTabs";
import { ChangePasswordButton, TeamTab } from "./TeamTab";

const Stats = () => {
  const { data } = useQuery({ queryKey: ["admin-stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });
  const items = [
    ["Total applications", data?.applications], ["Pending applications", data?.applications_pending],
    ["Open grievances", data?.grievances_open], ["Open legal requests", data?.legal_open],
    ["Events", data?.events], ["News articles", data?.news],
  ];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      {items.map(([l, v], i) => (
        <div key={l} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-stat-${i}`}>
          <p className="text-xs font-semibold text-slate-500">{l}</p><p className="mt-2 font-display text-3xl font-extrabold text-navy">{v ?? "–"}</p>
        </div>
      ))}
    </div>
  );
};

const TABS = [
  ["applications", "Applications", <ApplicationsTab />],
  ["grievances", "Grievances", <CasesTab endpoint="/admin/grievances" qkey="admin-grievances" />],
  ["legal", "Legal Requests", <CasesTab endpoint="/admin/legal-requests" qkey="admin-legal" />],
  ["news", "News", <NewsTab />],
  ["events", "Events", <EventsTab />],
  ["gallery", "Gallery", <GalleryTab />],
  ["documents", "Documents", <DocumentsTab />],
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => {
    document.title = "Admin | IT Employees Federation";
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() =>
      api.post("/auth/refresh").then(() => api.get("/auth/me")).then((r) => setUser(r.data)).catch(() => nav("/admin/login"))
    );
  }, [nav]);
  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  if (!user) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-royal" /></div>;
  const tabs = user.role === "admin" ? [...TABS, ["team", "Team", <TeamTab me={user} />]] : TABS;
  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-navy text-white">
        <div className="container-x flex h-16 items-center justify-between">
          <div className="flex items-center gap-3"><LogoMark className="h-9 w-9" /><span className="font-display font-bold">ITEF Admin</span></div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden items-center gap-1 text-slate-300 hover:text-white sm:flex" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />View site</Link>
            <span className="hidden text-slate-400 md:inline" data-testid="admin-user-email">{user.email} · {user.role === "admin" ? "Main admin" : "Staff"}</span>
            <ChangePasswordButton />
            <button onClick={logout} className="flex items-center gap-1 rounded-lg bg-white/10 px-3 py-1.5 hover:bg-white/20" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Logout</button>
          </div>
        </div>
      </header>
      <main className="container-x space-y-8 py-8">
        <Stats />
        <Tabs defaultValue="applications">
          <TabsList className="flex h-auto flex-wrap justify-start bg-white p-1">
            {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} className="data-[state=active]:bg-royal data-[state=active]:text-white" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
          {tabs.map(([v, , c]) => <TabsContent key={v} value={v} className="mt-6">{c}</TabsContent>)}
        </Tabs>
      </main>
    </div>
  );
}
